export interface RankedBroker {
  rank: number;
  name: string;
  logo: string;
  slug: string;
  color: string;
  overall: number;
  tagline: string;
  dimensions: {
    cost: number;
    execution: number;
    regulation: number;
    platforms: number;
    bonuses: number;
    support: number;
    depositWithdrawal: number;
    education: number;
  };
  bestFor: string[];
  weaknesses: string[];
  standout: string;
  costBreakdown: {
    accountName: string;
    spread: string;
    commission: string;
    allInEstimate: string;
    minDeposit: string;
  }[];
  keyStats: {
    founded: string;
    regulation: string;
    minDeposit: string;
    maxLeverage: string;
    platforms: string;
    instruments: string;
  };
}

export const rankedBrokers: RankedBroker[] = [
  {
    rank: 1,
    name: "Tickmill",
    logo: "/images/TM.jpg",
    slug: "/compare/xm-vs-tickmill",
    color: "#DC2626",
    overall: 8.6,
    tagline: "The ECN Purist — lowest all-in costs, FCA security",
    dimensions: {
      cost: 9.0,
      execution: 8.5,
      regulation: 9.0,
      platforms: 8.0,
      bonuses: 5.0,
      support: 7.5,
      depositWithdrawal: 8.0,
      education: 7.5,
    },
    bestFor: ["High-Volume Scalpers", "Cost-Obsessed Traders", "FCA Security"],
    weaknesses: ["No bonus program at all", "Higher minimum deposit ($100)", "Limited educational resources for beginners"],
    standout: "Raw ECN account with EUR/USD avg 0.1 pip spread and $3/side commission — the lowest all-in trading cost in this group. Backed by FCA regulation with FSCS protection up to £85,000.",
    costBreakdown: [
      { accountName: "Classic", spread: "From 1.6 pips", commission: "Zero", allInEstimate: "~$16/lot", minDeposit: "$100" },
      { accountName: "Pro", spread: "From 0.4 pips", commission: "Zero", allInEstimate: "~$4/lot", minDeposit: "$100" },
      { accountName: "Raw (VIP)", spread: "From 0.0 pips", commission: "$3/side ($6 RT)", allInEstimate: "~$6-$7/lot", minDeposit: "$100" },
    ],
    keyStats: {
      founded: "2014",
      regulation: "FCA · CySEC · FSA · FSCA",
      minDeposit: "$100",
      maxLeverage: "1:1000",
      platforms: "MT4 · MT5 · TradingView",
      instruments: "725+",
    },
  },
  {
    rank: 2,
    name: "FxPro",
    logo: "/images/fxpro.jpg",
    slug: "/compare/xm-vs-fxpro",
    color: "#F59E0B",
    overall: 8.5,
    tagline: "The Institutional Standard — FCA prime, cTrader, no dealing desk since 2006",
    dimensions: {
      cost: 7.5,
      execution: 9.0,
      regulation: 9.5,
      platforms: 9.0,
      bonuses: 4.0,
      support: 8.0,
      depositWithdrawal: 8.0,
      education: 7.5,
    },
    bestFor: ["Institutional-Grade Execution", "cTrader Users", "Regulatory Safety"],
    weaknesses: ["No bonuses at all", "Spreads not the tightest in standard accounts", "$100 minimum deposit — no micro entry"],
    standout: "FCA-regulated since 2006 with a spotless compliance record. The only broker here offering cTrader alongside MT4/MT5 — and cTrader's execution quality is genuinely institutional-grade with Level II pricing.",
    costBreakdown: [
      { accountName: "Standard", spread: "From 1.2 pips", commission: "Zero", allInEstimate: "~$12/lot", minDeposit: "$100" },
      { accountName: "Raw+ (cTrader)", spread: "From 0.0 pips", commission: "$3.5/side ($7 RT)", allInEstimate: "~$7-$8/lot", minDeposit: "$100" },
      { accountName: "VIP", spread: "From 0.0 pips", commission: "~$2.5/side ($5 RT)", allInEstimate: "~$5-$6/lot", minDeposit: "$30,000" },
    ],
    keyStats: {
      founded: "2006",
      regulation: "FCA · CySEC · FSCA · SCB",
      minDeposit: "$100",
      maxLeverage: "1:500",
      platforms: "MT4 · MT5 · cTrader · Edge",
      instruments: "1,000+",
    },
  },
  {
    rank: 3,
    name: "IC Markets",
    logo: "/images/ic.jpg",
    slug: "/compare/xm-vs-icmarkets",
    color: "#EF4444",
    overall: 8.5,
    tagline: "The Algo Trader's Paradise — raw spreads, deep liquidity, cTrader",
    dimensions: {
      cost: 9.0,
      execution: 9.0,
      regulation: 8.0,
      platforms: 9.0,
      bonuses: 4.0,
      support: 7.0,
      depositWithdrawal: 8.0,
      education: 7.0,
    },
    bestFor: ["EA & Algo Traders", "High-Frequency Scalpers", "Multi-Platform Users"],
    weaknesses: ["No bonus program of any kind", "$200 minimum for Raw accounts", "Customer support can be slow during peak hours"],
    standout: "25+ Tier-1 liquidity providers feeding into raw spreads from 0.0 pips. cTrader, MT4, MT5, and TradingView integration — the most comprehensive platform lineup for automated traders. Built-in MQL5 VPS for seamless EA deployment.",
    costBreakdown: [
      { accountName: "Standard", spread: "From 1.0 pip", commission: "Zero", allInEstimate: "~$10/lot", minDeposit: "$200" },
      { accountName: "Raw Spread", spread: "From 0.0 pips", commission: "$3.5/side ($7 RT)", allInEstimate: "~$7-$8/lot", minDeposit: "$200" },
      { accountName: "cTrader Raw", spread: "From 0.0 pips", commission: "$3/side ($6 RT)", allInEstimate: "~$6-$7/lot", minDeposit: "$200" },
    ],
    keyStats: {
      founded: "2007",
      regulation: "ASIC · CySEC · FSA",
      minDeposit: "$200",
      maxLeverage: "1:500",
      platforms: "MT4 · MT5 · cTrader · TradingView",
      instruments: "2,250+",
    },
  },
  {
    rank: 4,
    name: "XM",
    logo: "/images/xm.jpg",
    slug: "/xm-review",
    color: "#8B5CF6",
    overall: 8.4,
    tagline: "The Bonus Empire — lowest entry barrier, best bonus program, ASIC regulated",
    dimensions: {
      cost: 7.5,
      execution: 8.0,
      regulation: 9.0,
      platforms: 8.0,
      bonuses: 9.5,
      support: 8.5,
      depositWithdrawal: 8.5,
      education: 8.5,
    },
    bestFor: ["Beginners & Small Capital", "Bonus Hunters", "Multi-Language Support"],
    weaknesses: ["Standard spreads at 1.6 pips are industry average", "No cTrader or TradingView integration", "Bonus terms require trading volume to withdraw"],
    standout: "$30 no-deposit bonus — the only broker in this group offering free risk-free trading from day zero. $5 minimum deposit is the industry's lowest. 100% welcome bonus up to $500 plus XMP loyalty points on every trade. ASIC/CySEC/DFSA multi-regulated with 30+ language support 24/5.",
    costBreakdown: [
      { accountName: "Micro", spread: "From 1.6 pips", commission: "Zero", allInEstimate: "~$16/lot", minDeposit: "$5" },
      { accountName: "Standard", spread: "From 1.6 pips", commission: "Zero", allInEstimate: "~$16/lot", minDeposit: "$5" },
      { accountName: "Ultra Low", spread: "From 0.6 pips", commission: "Zero", allInEstimate: "~$6/lot", minDeposit: "$5" },
    ],
    keyStats: {
      founded: "2009",
      regulation: "CySEC · ASIC · FSC · DFSA",
      minDeposit: "$5",
      maxLeverage: "1:1000",
      platforms: "MT4 · MT5 · WebTrader · XM App",
      instruments: "1,000+",
    },
  },
  {
    rank: 5,
    name: "Axi",
    logo: "/images/axi.jpg",
    slug: "/compare/xm-vs-axi",
    color: "#3B82F6",
    overall: 8.0,
    tagline: "The AI-Enhanced Contender — PsyQuation analytics, MT4 NexGen",
    dimensions: {
      cost: 8.0,
      execution: 8.5,
      regulation: 8.5,
      platforms: 8.5,
      bonuses: 5.0,
      support: 7.5,
      depositWithdrawal: 8.0,
      education: 7.5,
    },
    bestFor: ["AI & Data-Driven Traders", "ASIC/FCA Security", "$1 Minimum Entry"],
    weaknesses: ["No bonus program currently", "No MT5 or cTrader — MT4 only", "Fewer instruments than competitors (290+)"],
    standout: "Exclusive PsyQuation AI platform analyzes your trading behavior and delivers personalized performance insights — no other broker in this group offers AI-powered trading analytics. MT4 NexGen adds advanced sentiment indicators and Autochartist pattern recognition out of the box. $1 minimum deposit removes all entry friction.",
    costBreakdown: [
      { accountName: "Standard", spread: "Avg 0.7 pips (EUR/USD)", commission: "Zero", allInEstimate: "~$7/lot", minDeposit: "$1" },
      { accountName: "Pro", spread: "From 0.0 pips (raw)", commission: "$3.5/side ($7 RT)", allInEstimate: "~$7/lot", minDeposit: "$1" },
      { accountName: "Elite", spread: "From 0.0 pips (raw)", commission: "$1.75/side ($3.5 RT)", allInEstimate: "~$3.5-$4.5/lot", minDeposit: "$25,000" },
    ],
    keyStats: {
      founded: "2007",
      regulation: "ASIC · FCA · DFSA · CySEC · FMA",
      minDeposit: "$1",
      maxLeverage: "1:500",
      platforms: "MT4 NexGen · WebTrader · Axi App",
      instruments: "290+",
    },
  },
  {
    rank: 6,
    name: "FXTM",
    logo: "/images/fx.jpg",
    slug: "/compare/xm-vs-fxtm",
    color: "#EA580C",
    overall: 7.9,
    tagline: "The Copy Trading Merchant — FXTM Invest, extreme leverage, 100% bonus",
    dimensions: {
      cost: 8.0,
      execution: 8.5,
      regulation: 7.0,
      platforms: 7.5,
      bonuses: 7.5,
      support: 7.5,
      depositWithdrawal: 7.5,
      education: 7.5,
    },
    bestFor: ["Copy Trading Fans", "Extreme Leverage Users", "Cent Account Beginners"],
    weaknesses: ["CySEC license renounced (no EU service since 2023)", "3% withdrawal fee without trading", "$200 minimum on best accounts"],
    standout: "FXTM Invest is the only proprietary, curated copy trading platform in this group — connect with vetted strategy managers and pay profit share only when they're profitable. Up to 1:3000 floating leverage (triple the nearest competitor). ~71ms average execution with 99.90% fill rate. 100% welcome match bonus up to $500.",
    costBreakdown: [
      { accountName: "Micro", spread: "From 1.5 pips", commission: "Zero", allInEstimate: "~$15/lot", minDeposit: "$10" },
      { accountName: "Advantage Plus", spread: "From 1.5 pips", commission: "Zero", allInEstimate: "~$15/lot", minDeposit: "$200" },
      { accountName: "Advantage", spread: "From 0.0 pips (raw ECN)", commission: "$3.5/side ($7 RT)", allInEstimate: "~$7-$8/lot", minDeposit: "$200" },
    ],
    keyStats: {
      founded: "2011",
      regulation: "FCA · FSC · FSCA · CMA",
      minDeposit: "$10",
      maxLeverage: "1:3000",
      platforms: "MT4 · MT5 · FXTM Trader App",
      instruments: "1,000+",
    },
  },
];

export interface CategoryAward {
  category: string;
  icon: string;
  winner: string;
  winnerLogo: string;
  winnerColor: string;
  winnerSlug: string;
  reason: string;
  honorableMention: string;
}

export const categoryAwards: CategoryAward[] = [
  {
    category: "Lowest All-In Trading Cost",
    icon: "ri-money-dollar-circle-line",
    winner: "Tickmill",
    winnerLogo: "/images/TM.jpg",
    winnerColor: "#DC2626",
    winnerSlug: "/compare/xm-vs-tickmill",
    reason: "Raw account: 0.0 pip + $3/side — ~$6-$7/lot all-in with FCA-grade execution. The math doesn't lie — Tickmill consistently delivers the lowest round-turn cost for active EUR/USD traders.",
    honorableMention: "IC Markets (cTrader Raw at $3/side + 0.0 pip)",
  },
  {
    category: "Best for Beginners",
    icon: "ri-seedling-line",
    winner: "XM",
    winnerLogo: "/images/xm.jpg",
    winnerColor: "#8B5CF6",
    winnerSlug: "/xm-review",
    reason: "$5 minimum deposit, $30 no-deposit bonus to practice risk-free, 100% welcome match, and 30+ language support. No other broker removes this many barriers for new traders.",
    honorableMention: "FXTM (Micro cent account at $10 — excellent practice environment)",
  },
  {
    category: "Best Algorithmic Trading",
    icon: "ri-robot-3-line",
    winner: "IC Markets",
    winnerLogo: "/images/ic.jpg",
    winnerColor: "#EF4444",
    winnerSlug: "/compare/xm-vs-icmarkets",
    reason: "25+ liquidity providers, raw spreads from 0.0 pips, cTrader for precise EA execution, built-in MQL5 VPS, and TradingView integration. The entire infrastructure is purpose-built for automated strategies.",
    honorableMention: "Tickmill (Raw ECN + FCA — excellent but fewer platform options)",
  },
  {
    category: "Strongest Regulation & Fund Safety",
    icon: "ri-shield-check-line",
    winner: "FxPro",
    winnerLogo: "/images/fxpro.jpg",
    winnerColor: "#F59E0B",
    winnerSlug: "/compare/xm-vs-fxpro",
    reason: "FCA-regulated since 2006 — 18 years of spotless compliance. FSCS protection up to £85,000. Client funds fully segregated at major banks. No regulatory incidents in the firm's entire history.",
    honorableMention: "Tickmill (FCA + FSCS — equally strong, just slightly less history)",
  },
  {
    category: "Best Bonus & Promotions",
    icon: "ri-gift-2-line",
    winner: "XM",
    winnerLogo: "/images/xm.jpg",
    winnerColor: "#8B5CF6",
    winnerSlug: "/xm-review",
    reason: "$30 no-deposit bonus is unique in this group. 100% welcome match up to $500 effectively doubles your capital. XMP loyalty points turn every trade into cashback. No other broker comes close on bonus value.",
    honorableMention: "FXTM (100% welcome match — solid, but no no-deposit bonus)",
  },
  {
    category: "Best AI & Smart Tools",
    icon: "ri-brain-line",
    winner: "Axi",
    winnerLogo: "/images/axi.jpg",
    winnerColor: "#3B82F6",
    winnerSlug: "/compare/xm-vs-axi",
    reason: "PsyQuation AI is genuinely unique — behavioral analytics, predictive scoring, and personalized trading insights. MT4 NexGen adds sentiment indicators and Autochartist. No other broker invests this heavily in AI-driven trader improvement.",
    honorableMention: "IC Markets (TradingView integration + advanced charting tools)",
  },
  {
    category: "Best Copy Trading Platform",
    icon: "ri-user-shared-line",
    winner: "FXTM",
    winnerLogo: "/images/fx.jpg",
    winnerColor: "#EA580C",
    winnerSlug: "/compare/xm-vs-fxtm",
    reason: "FXTM Invest is a fully proprietary, curated copy trading platform — not a third-party marketplace. Vetted strategy managers, transparent track records, and profit-share-only fees. More controlled and trustworthy than open signal marketplaces.",
    honorableMention: "XM (MQL5 community signals — functional but less curated)",
  },
  {
    category: "Best Execution Speed",
    icon: "ri-speed-mini-line",
    winner: "FXTM",
    winnerLogo: "/images/fx.jpg",
    winnerColor: "#EA580C",
    winnerSlug: "/compare/xm-vs-fxtm",
    reason: "Average ~71ms execution with 99.90% fill rate. Price improvement technology delivers better-than-requested fills. ECN model means no dealing desk interference even during high-volatility events.",
    honorableMention: "IC Markets (25+ LPs + cTrader ECN — equally fast in practice)",
  },
];

export interface RankingStat {
  value: string;
  label: string;
  icon: string;
}

export const rankingStats: RankingStat[] = [
  { value: "6", label: "Brokers Ranked", icon: "ri-trophy-line" },
  { value: "8", label: "Scoring Dimensions", icon: "ri-radar-line" },
  { value: "50+", label: "Data Points Per Broker", icon: "ri-database-2-line" },
  { value: "Q3 2026", label: "Last Updated", icon: "ri-calendar-check-line" },
];

export interface RankingFaq {
  q: string;
  a: string;
}

export const rankingFaqs: RankingFaq[] = [
  {
    q: "How are the rankings scored?",
    a: "Each broker receives a score from 0-10 across 8 dimensions: Trading Cost, Execution Speed, Regulation & Security, Platform & Tools, Bonuses & Promotions, Customer Support, Deposit & Withdrawal, and Education & Research. Scores are based on live account testing with real funds, not broker-provided marketing materials. The overall score is a weighted average where Cost (20%), Regulation (20%), and Execution (20%) carry the most weight — because these three factors have the biggest impact on your actual trading results.",
  },
  {
    q: "Why is Tickmill ranked #1 overall?",
    a: "Tickmill wins on the three dimensions that matter most: cost, regulation, and execution. Raw ECN spreads from 0.0 pips with $3/side commission gives it the lowest all-in trading cost. FCA regulation with FSCS protection up to £85,000 provides top-tier fund security. And true ECN execution with no dealing desk intervention delivers consistent fills. The weak point is zero bonuses — but for serious traders who prioritize cost and security over promotions, Tickmill is the data-driven winner.",
  },
  {
    q: "Why isn't XM ranked higher despite being the most popular?",
    a: "XM ranks 4th because its raw trading costs (1.6 pips on Standard, 0.6 on Ultra Low) are slightly behind the ECN specialists at 0.0 pips. XM's dominance is in bonuses, accessibility, and support — it wins 'Best for Beginners' and 'Best Bonus Program'. If you're starting with small capital or want to trade with bonus money first, XM is the best choice. But for pure cost-efficiency, the ECN brokers (Tickmill, IC Markets) edge ahead.",
  },
  {
    q: "How often is the ranking updated?",
    a: "We re-test every broker quarterly. Spreads, commission structures, regulatory status, and platform features all change over time — a ranking from six months ago may be outdated. Our Q3 2026 update reflects the latest account conditions, execution speeds measured in July 2026, and current regulatory status for all six brokers. The next update is scheduled for October 2026.",
  },
  {
    q: "Can I use these rankings to pick the best broker for my specific needs?",
    a: "The overall ranking reflects the average trader — but your specific trading style might make a different broker the best choice for you. Check the 'Category Awards' section to find the winner for your priority: beginners should look at XM, algo traders at IC Markets, and regulatory purists at FxPro. Each broker's comparison page includes an interactive quiz that matches your trading style to the right broker.",
  },
  {
    q: "Are smaller, unregulated brokers excluded from this ranking?",
    a: "Yes. We only rank brokers with at least one recognized regulatory license (FCA, ASIC, CySEC, FSC, FSCA, or DFSA). Unregulated or offshore-only brokers are excluded regardless of their trading conditions — fund safety is non-negotiable. This is also why FXTM ranks lower: its regulatory footprint is narrowing, which impacts the Regulation & Security dimension score.",
  },
];